import { Injectable } from '@angular/core';
import { DirtyCheckPlugin, Query } from '@datorama/akita';
import { defaultValues } from '@shared/models/Mappers/default-values-mapper';
import { BridgeService } from '@shared/services/bridge.service';
import { ValidateRxService } from '@shared/services/validate-rx';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TeethDiagramState } from '../models/teeth-diagram';
import { TeethNumberingSystem } from '../models/teeth-numbering-system.enum';
import { Tooth } from '../models/tooth';
import { UnitTypes } from '../models/unit-type.enum';
import { TeethDiagramStore } from './teeth-diagram-store';
import { ObjectUtils } from '@shared/utils/object-utils/object-utils';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';
import { ToothApi } from '../models/tooth-api';
import { CustomEntityForSave } from '@shared/models/custom-material';
import { customMaterialBackendId, customSpecificationBackendId } from '@shared/models/consts';

@Injectable({ providedIn: 'root' })
export class TeethDiagramQuery extends Query<TeethDiagramState> {
	teethState$: Observable<TeethDiagramState> = this.select(state => state);
	teeth$: Observable<{ lowerJaw: Tooth[]; upperJaw: Tooth[] }> = this.select(state => state.teeth);
	upperJaw$: Observable<Tooth[]> = this.select(state => state.teeth.upperJaw);
	lowerJaw$: Observable<Tooth[]> = this.select(state => state.teeth.lowerJaw);
	teethNumberingSystem$: Observable<TeethNumberingSystem> = this.select(state => state.teethNumberingSystem);
	forceDisplayTeethNumberingSystem$: Observable<TeethNumberingSystem | undefined> = this.select(
		state => state.forceDisplayTeethNumberingSystem
	);
	copiedToothId$: Observable<number | null> = this.select(state => state.copiedToothId);

	teethFlat$: Observable<Tooth[]> = this.select(state => [...state.teeth.upperJaw, ...state.teeth.lowerJaw]);

	teethRecord$: Observable<Record<number, Tooth>> = this.teethFlat$.pipe(map(teeth => this.buildTeethRecord(teeth)));

	private readonly teethDiagramDirtyCheck: DirtyCheckPlugin;

	get teethFlat(): Tooth[] {
		const { upperJaw, lowerJaw } = this.getValue().teeth;

		return [...upperJaw, ...lowerJaw];
	}

	get teethRecord(): Record<number, Tooth> {
		return this.buildTeethRecord(this.teethFlat);
	}

	get teeth(): { lowerJaw: Tooth[]; upperJaw: Tooth[] } {
		return this.getValue().teeth;
	}

	get hasImplantBasedPrep(): boolean {
		const teeth = this.getValue().teeth;

		return this.getHasImplantBasedPrep(teeth);
	}

	getToothBackendModel(
		tooth: Tooth,
		customMaterial: CustomEntityForSave | undefined,
		customSpecification: CustomEntityForSave | undefined
	): ToothApi {
		const isBridgeTooth = !!tooth.BridgeIndex;
		const toothWithBackendValues = this.getToothWithBackendValues(tooth, isBridgeTooth, customMaterial, customSpecification);

		return this.replaceNullsByUndefined(toothWithBackendValues); // "undefined" in TypeScript --> "null" in CSharp
	}

	getToothWithBackendValues(
		tooth: Tooth,
		isBridgeTooth: boolean,
		customMaterial: CustomEntityForSave | undefined,
		customSpecification: CustomEntityForSave | undefined
	): ToothApi {
		const unitTypeId = tooth.UnitTypeID || defaultValues.UnitTypeID.serverDefault;

		let result: ToothApi = {
			...tooth,
			UnitTypeID: isBridgeTooth
				? this.bridgeService.convertFromBridgeToothUnitType({ unitTypeInBridge: tooth.ToothInBridgeTypeID })
				: unitTypeId,
			MaterialID: this.getToothiTeroMaterialEntityIdWithDefault(tooth.MaterialID, defaultValues.MaterialID.serverDefault),
			SpecificationId: this.getToothiTeroMaterialEntityIdWithDefault(
				tooth.SpecificationId,
				defaultValues.SpecificationId.serverDefault
			),
			ShadeSystemId: tooth.ShadeSystemId || defaultValues.ShadeSystemId.serverDefault,
			PonticDesignID: tooth.PonticDesignID || defaultValues.PonticDesignID.serverDefault,
			ImplantBasedRestorationTypeId: tooth.ImplantBasedRestorationTypeId || defaultValues.ImplantBasedRestorationTypeId.serverDefault
		} as ToothApi;

		if (customMaterial) {
			result = {
				...result,
				MaterialID: customMaterialBackendId,
				...this.getCustomMaterialBackendModel(customMaterial)
			};
		}

		if (customSpecification) {
			result = {
				...result,
				SpecificationId: customSpecificationBackendId,
				...this.getCustomSpecificationBackendModel(customSpecification)
			};
		}

		return result;
	}

	get isInvalidForSendTreatedTeeth(): boolean {
		const { upperJaw, lowerJaw } = this.getValue().teeth;
		const invalidTooth = this.getTreatedTeeth(upperJaw, lowerJaw).find((tooth: Tooth) => {
			return this.validateRxService.isTreatmentInvalidForSend(tooth);
		});

		return !!invalidTooth;
	}

	get isValidForSendTreatedTeeth(): boolean {
		return !this.isInvalidForSendTreatedTeeth;
	}

	get teethNumberingSystem(): TeethNumberingSystem {
		return this.getValue().teethNumberingSystem;
	}

	get copiedToothId(): string | number | null {
		return this.getValue().copiedToothId;
	}

	get isDirty(): boolean {
		return this.teethDiagramDirtyCheck.isDirty();
	}

	/**
	 * Re-baseline the dirty check to the current store state so subsequent {@link isDirty} calls report
	 * changes relative to *now*. Call this after a display-only store write that should not count as unsaved
	 * work (e.g. syncing `teethNumberingSystem` to the user's setting), otherwise the whole-store dirty
	 * check would flag an untouched diagram as dirty and wrongly trigger the send-to-change confirmation.
	 */
	resetDirtyCheckHead(): void {
		this.teethDiagramDirtyCheck.setHead();
	}

	getTreatedTeeth(upperJaw: Tooth[], lowerJaw: Tooth[]): Tooth[] {
		const teeth = [...upperJaw, ...lowerJaw];

		return teeth.filter(tooth => {
			const isBridgeTooth = !!tooth.BridgeIndex;
			const isTreatedTooth =
				(tooth.UnitTypeID as number) !== defaultValues.UnitTypeID.serverDefault && tooth.UnitTypeID !== UnitTypes.Regular;

			return isBridgeTooth || isTreatedTooth || tooth.ArtTreatment != null;
		});
	}
	getHasImplantBasedPrep({ lowerJaw, upperJaw }: { lowerJaw: Tooth[]; upperJaw: Tooth[] }): boolean {
		return [...upperJaw, ...lowerJaw].some(
			tooth => tooth.UnitTypeID === UnitTypes.ImplantBased || tooth.ToothInBridgeTypeID === UnitTypesInBridge.ImplantBased
		);
	}

	private getToothiTeroMaterialEntityIdWithDefault(originalId: number | string | null | undefined, serverDefault: number) {
		return !!originalId && typeof originalId === 'number' ? originalId : serverDefault;
	}

	private getCustomSpecificationBackendModel(customSpecification: CustomEntityForSave | null): {
		CustomLabSpecificationId?: number;
		CustomLabSpecificationName?: string;
		CustomLabSpecificationRevisionId?: number;
	} {
		return !customSpecification
			? {}
			: {
					CustomLabSpecificationId: customSpecification.id,
					CustomLabSpecificationName: customSpecification.name,
					CustomLabSpecificationRevisionId: customSpecification.lastRevisionId
				};
	}

	private getCustomMaterialBackendModel(customMaterial: CustomEntityForSave | null): {
		CustomLabMaterialId?: number;
		CustomLabMaterialName?: string;
		CustomLabMaterialRevisionId?: number;
		CustomLabMaterialGroup?: string;
	} {
		return !customMaterial
			? {}
			: {
					CustomLabMaterialId: customMaterial.id,
					CustomLabMaterialName: customMaterial.name,
					CustomLabMaterialRevisionId: customMaterial.lastRevisionId,
					CustomLabMaterialGroup: customMaterial.materialGroup
				};
	}

	private buildTeethRecord(teeth: Tooth[]): Record<number, Tooth> {
		return teeth.reduce<Record<number, Tooth>>((acc, tooth) => {
			if (tooth.ToothID != null) {
				acc[tooth.ToothID] = tooth;
			}

			return acc;
		}, {});
	}

	private replaceNullsByUndefined<T extends Record<string, any>>(tooth: T): T {
		return ObjectUtils.mapObject(tooth, ([, value]) => (value === null ? undefined : value) as any);
	}

	constructor(
		store: TeethDiagramStore,
		private bridgeService: BridgeService,
		private validateRxService: ValidateRxService
	) {
		super(store);
		this.teethDiagramDirtyCheck = new DirtyCheckPlugin(this).setHead();
	}
}
